import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, SearchIcon, CheckCircleIcon, ClockIcon, XCircleIcon, MoreHorizontalIcon, EyeIcon, EditIcon, ArchiveIcon, MailIcon, DownloadIcon, CalendarIcon, UserIcon, ChevronRightIcon, FilterIcon, SlidersIcon } from 'lucide-react';
// Order status types
export type OrderStatus = 'new' | 'in-progress' | 'open' | 'completed' | 'cancelled';
// Order type definition
export interface Order {
  id: string;
  status: OrderStatus;
  paymentStatus: 'paid' | 'unpaid' | 'cancelled';
  customer: {
    name: string;
    email: string;
    address: string;
    mobileNumber: string;
    additionalPhone?: string;
    address2?: string;
  };
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  total: number;
  date: string;
  time: string;
  transaction?: string;
  transactionStatus?: 'confirmed' | 'pending' | 'failed';
  notes?: string;
}
// Mock order data
const mockOrders: Order[] = [{
  id: '220629-1e753',
  status: 'new',
  paymentStatus: 'paid',
  customer: {
    name: 'randy',
    email: 'randy@randy.com',
    address: 'randy',
    mobileNumber: '6596605522',
    additionalPhone: '1234',
    address2: '1234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '29/06/2022',
  time: '14:28',
  transaction: '62bc1b4be3c2250009f1e753',
  transactionStatus: 'confirmed'
}, {
  id: '210505-00003',
  status: 'new',
  paymentStatus: 'cancelled',
  customer: {
    name: 'SOWMIYA',
    email: 'sowme76@gmail.com',
    address: 'ADDESS1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 3,
    price: 110.0
  }],
  total: 330.0,
  date: '05/05/2021',
  time: '07:52'
}, {
  id: '210506-00004',
  status: 'in-progress',
  paymentStatus: 'paid',
  customer: {
    name: 'sowmiya',
    email: 'sowme76@gmail.com',
    address: 'Address Line 1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '06/05/2021',
  time: '12:48'
}, {
  id: '210504-00002',
  status: 'in-progress',
  paymentStatus: 'cancelled',
  customer: {
    name: 'sowmiya',
    email: 'sowmiya@pomel.com',
    address: 'Address Line 1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '04/05/2021',
  time: '16:06'
}, {
  id: '210503-00001',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'John Doe',
    email: 'john@example.com',
    address: 'Male, Maldives',
    mobileNumber: '9876543210'
  },
  items: [{
    name: 'Kittens',
    quantity: 1,
    price: 111.0
  }],
  total: 111.0,
  date: '03/05/2021',
  time: '10:30'
}, {
  id: '210502-00005',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'Jane Smith',
    email: 'jane@example.com',
    address: 'Male, Maldives',
    mobileNumber: '9876543211'
  },
  items: [{
    name: 'Kittens',
    quantity: 1,
    price: 111.0
  }],
  total: 111.0,
  date: '02/05/2021',
  time: '11:15'
}, {
  id: '210501-00006',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'Alex Brown',
    email: 'alex@example.com',
    address: 'Male, Maldives',
    mobileNumber: '9876543212'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '01/05/2021',
  time: '09:45'
}];
export function Orders() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<OrderStatus | 'all'>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const getStatusBadge = (status: OrderStatus) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-amber-100 text-amber-800',
      open: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      new: 'New',
      'in-progress': 'In Progress',
      open: 'Open',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-9558">
        {labels[status]}
      </span>;
  };
  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon size={14} className="text-green-500" data-id="element-9559" />;
      case 'unpaid':
        return <ClockIcon size={14} className="text-amber-500" data-id="element-9560" />;
      case 'cancelled':
        return <XCircleIcon size={14} className="text-red-500" data-id="element-9561" />;
      default:
        return null;
    }
  };
  const filteredOrders = mockOrders.filter(order => {
    const matchesSearch = searchQuery === '' || order.id.toLowerCase().includes(searchQuery.toLowerCase()) || order.customer.name.toLowerCase().includes(searchQuery.toLowerCase()) || order.customer.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = activeTab === 'all' || order.status === activeTab;
    return matchesSearch && matchesStatus;
  });
  const totalAmount = filteredOrders.reduce((sum, order) => sum + order.total, 0);
  const completedAmount = filteredOrders.filter(order => order.status === 'completed').reduce((sum, order) => sum + order.total, 0);
  const cancelledAmount = filteredOrders.filter(order => order.status === 'cancelled').reduce((sum, order) => sum + order.total, 0);
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-9562">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-9563">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-9564">Orders</h1>
        <div className="flex items-center gap-3" data-id="element-9565">
          <button onClick={() => window.location.href = '/settings?tab=order-settings'} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-9566">
            Settings
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-9567">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-9568">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-9569">
            <ShoppingCartIcon size={16} className="text-gray-500" data-id="element-9570" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-9571">
              Total Orders
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-9572">
            <div className="text-2xl font-bold text-gray-900" data-id="element-9573">
              {filteredOrders.length}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-9574">
            All time
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-9575">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-9576">
            <span className="text-gray-500 font-medium" data-id="element-9577">$</span>
            <span className="text-sm font-semibold text-gray-900" data-id="element-9578">
              Total Amount
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-9579">
            <div className="text-2xl font-bold text-gray-900" data-id="element-9580">
              $
              {totalAmount.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-9581">
            Across {filteredOrders.length} orders
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-9582">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-9583">
            <CheckCircleIcon size={16} className="text-green-500" data-id="element-9584" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-9585">
              Completed
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-9586">
            <div className="text-2xl font-bold text-green-600" data-id="element-9587">
              $
              {completedAmount.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
            </div>
          </div>
          <div className="px-4 py-2 bg-green-50 text-xs text-green-700 font-medium" data-id="element-9588">
            {filteredOrders.filter(o => o.status === 'completed').length}{' '}
            orders
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-9589">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-9590">
            <XCircleIcon size={16} className="text-red-500" data-id="element-9591" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-9592">
              Cancelled
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-9593">
            <div className="text-2xl font-bold text-red-600" data-id="element-9594">
              $
              {cancelledAmount.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
            </div>
          </div>
          <div className="px-4 py-2 bg-red-50 text-xs text-red-700 font-medium" data-id="element-9595">
            {filteredOrders.filter(o => o.status === 'cancelled').length}{' '}
            orders
          </div>
        </div>
      </div>

      <div className="space-y-6" data-id="element-9596">
        {/* Tabs + Controls */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between border-b border-gray-200 gap-4" data-id="element-9597">
          <div className="flex space-x-6 overflow-x-auto pb-1" data-id="element-9598">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'new',
            label: 'New'
          }, {
            id: 'in-progress',
            label: 'In Progress'
          }, {
            id: 'open',
            label: 'Open'
          }, {
            id: 'completed',
            label: 'Completed'
          }, {
            id: 'cancelled',
            label: 'Cancelled'
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as OrderStatus | 'all')} className={`pb-3 -mb-px text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === tab.id ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-9599">
                {tab.label}
              </button>)}
          </div>
          <div className="flex items-center gap-4 pb-3" data-id="element-9600">
            <div className="relative" data-id="element-9601">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-9602" />
              <input type="text" placeholder="Search orders..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full sm:w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-9603" />
            </div>
            <button className="flex items-center px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors" data-id="element-9604">
              <SlidersIcon size={14} className="mr-2" data-id="element-9605" />
              <span className="text-sm font-medium" data-id="element-9606">Filters</span>
            </button>
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-9607">
            <p className="text-gray-500 text-sm" data-id="element-9608">
              No orders found matching your criteria.
            </p>
          </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-9609">
            <div className="overflow-x-auto" data-id="element-9610">
              <table className="w-full text-left border-collapse" data-id="element-9611">
                <thead data-id="element-9612">
                  <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-9613">
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9614">
                      Order
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9615">
                      Date
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9616">
                      Customer
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9617">
                      Status
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9618">
                      Payment
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9619">
                      Total
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9620">
                      Items
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-9621">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-9622">
                  {filteredOrders.map(order => <tr key={order.id} onClick={() => navigate(`/order-detail/${order.id}`)} className="hover:bg-gray-50 transition-colors cursor-pointer group" data-id="element-9623">
                      <td className="px-5 py-4" data-id="element-9624">
                        <div className="text-sm font-semibold text-gray-900 group-hover:text-[#F7941D] transition-colors" data-id="element-9625">
                          #{order.id}
                        </div>
                        {order.transaction && <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[120px]" data-id="element-9626">
                            {order.transaction}
                          </div>}
                      </td>
                      <td className="px-5 py-4" data-id="element-9627">
                        <div className="flex items-center text-sm text-gray-900" data-id="element-9628">
                          <CalendarIcon size={14} className="mr-1.5 text-gray-400" data-id="element-9629" />
                          {order.date}
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mt-0.5" data-id="element-9630">
                          <ClockIcon size={14} className="mr-1.5 text-gray-400" data-id="element-9631" />
                          {order.time}
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-9632">
                        <div className="text-sm font-medium text-gray-900" data-id="element-9633">
                          {order.customer.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[150px]" data-id="element-9634">
                          {order.customer.email}
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-9635">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-5 py-4" data-id="element-9636">
                        <div className="flex items-center" data-id="element-9637">
                          {getPaymentStatusIcon(order.paymentStatus)}
                          <span className="ml-1.5 text-sm text-gray-900 capitalize" data-id="element-9638">
                            {order.paymentStatus}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-9639">
                        <div className="text-sm font-medium text-gray-900" data-id="element-9640">
                          ${order.total.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-9641">
                        <div className="text-sm text-gray-900" data-id="element-9642">
                          {order.items.reduce((sum, item) => sum + item.quantity, 0)}{' '}
                          items
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[120px]" data-id="element-9643">
                          {order.items.map(item => item.name).join(', ')}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right relative" data-id="element-9644">
                        <div ref={contextMenuOpenId === order.id ? contextMenuRef : null} data-id="element-9645">
                          <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(contextMenuOpenId === order.id ? null : order.id);
                    }} data-id="element-9646">
                            <MoreHorizontalIcon size={18} data-id="element-9647" />
                          </button>

                          {contextMenuOpenId === order.id && <>
                              <div className="fixed inset-0 z-10" onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                      }} data-id="element-9648" />
                              <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-9649">
                                <button onClick={e => {
                          e.stopPropagation();
                          setContextMenuOpenId(null);
                          navigate(`/order-detail/${order.id}`);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9650">
                                  <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-9651" />
                                  View Details
                                </button>
                                <button onClick={e => {
                          e.stopPropagation();
                          setContextMenuOpenId(null);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9652">
                                  <MailIcon size={16} className="mr-3 text-gray-400" data-id="element-9653" />
                                  Email Customer
                                </button>
                                <div className="border-t border-gray-100 my-1" data-id="element-9654" />
                                <button onClick={e => {
                          e.stopPropagation();
                          setContextMenuOpenId(null);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-9655">
                                  <ArchiveIcon size={16} className="mr-3" data-id="element-9656" />
                                  Archive Order
                                </button>
                              </div>
                            </>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}